/**
 * VK Platform
 * 
 * Интеграция с VK Bridge для Mini App
 */

import bridge, { UserInfo } from '@vkontakte/vk-bridge';

interface VKUser {
  id: number;
  first_name: string;
  last_name: string;
  photo_100: string;
  photo_200: string;
}

class VKPlatform {
  private user: VKUser | null = null;
  private initialized = false;
  
  /**
   * Инициализация VK Mini App
   */
  async init(): Promise<void> {
    if (this.initialized) return;
    
    try {
      await bridge.send('VKWebAppInit');
      this.initialized = true;
      console.log('[VK] Initialized');
    } catch (error) {
      console.error('[VK] Init error:', error);
    }
  }
  
  /**
   * Получить данные пользователя
   */
  async getUser(): Promise<VKUser> {
    if (this.user) return this.user;
    
    try {
      const result = await bridge.send('VKWebAppGetUserInfo') as UserInfo;
      this.user = {
        id: result.id,
        first_name: result.first_name,
        last_name: result.last_name,
        photo_100: result.photo_100 || '',
        photo_200: result.photo_200 || '',
      };
      console.log('[VK] User:', this.user.first_name, this.user.id);
      return this.user;
    } catch (error) {
      console.error('[VK] GetUser error:', error);
      throw error;
    }
  }
  
  /**
   * ID пользователя
   */
  getUserId(): number | null {
    return this.user?.id || null;
  }
  
  /**
   * Имя пользователя
   */
  getDisplayName(): string {
    if (!this.user) return 'Гость';
    return `${this.user.first_name} ${this.user.last_name}`.trim();
  }
  
  /**
   * Аватар пользователя
   */
  getAvatar(): string | null {
    return this.user?.photo_100 || this.user?.photo_200 || null;
  }
  
  /**
   * Хаптик-фидбек (вибрация)
   */
  async haptic(type: 'impact' | 'notification' | 'selection' = 'selection'): Promise<void> {
    try {
      if (type === 'impact') {
        await bridge.send('VKWebAppTapticImpactOccurred', { style: 'light' });
      } else if (type === 'notification') {
        await bridge.send('VKWebAppTapticNotificationOccurred', { type: 'success' });
      } else {
        await bridge.send('VKWebAppTapticSelectionChanged');
      }
    } catch {
      // Игнорируем ошибки хаптика
    }
  }
  
  /**
   * «Поделиться ссылкой» в VK.
   *
   * VKWebAppShare показывает диалог с предложением запостить на стену —
   * это запрещено правилами Mini App. Поэтому здесь ссылка копируется
   * в буфер обмена через VKWebAppCopyText и отправляется тост.
   */
  async share(link: string): Promise<boolean> {
    try {
      await bridge.send('VKWebAppCopyText', { text: link });
      window.dispatchEvent(new CustomEvent('app-toast', {
        detail: { message: 'Ссылка скопирована' },
      }));
      return true;
    } catch {
      return false;
    }
  }
  
  /**
   * Показать нативный алерт
   */
  async showAlert(message: string): Promise<void> {
    // Используем in-app уведомление вместо browser alert
    const event = new CustomEvent('app-toast', { detail: { message } });
    window.dispatchEvent(event);
  }
  
  /**
   * Закрыть приложение
   */
  async close(): Promise<void> {
    try {
      await bridge.send('VKWebAppClose', { status: 'success' });
    } catch {
      // Игнорируем
    }
  }
  
  /**
   * Добавить в избранное
   */
  async addToFavorites(): Promise<boolean> {
    try {
      const result = await bridge.send('VKWebAppAddToFavorites');
      return result.result === true;
    } catch {
      return false;
    }
  }
  
  /**
   * Разрешить уведомления
   */
  async allowNotifications(): Promise<boolean> {
    try {
      const result = await bridge.send('VKWebAppAllowNotifications');
      return result.result === true;
    } catch {
      return false;
    }
  }
  
  /**
   * Получить launch params из URL
   */
  getLaunchParams(): Record<string, string> {
    const params: Record<string, string> = {};
    const search = window.location.search;
    
    if (search) {
      const urlParams = new URLSearchParams(search);
      urlParams.forEach((value, key) => {
        params[key] = value;
      });
    }
    
    return params;
  }
  
  /**
   * Получить hash из URL (для deeplinks)
   */
  getHash(): string {
    return window.location.hash.replace('#', '');
  }
}

// Singleton
export const vkPlatform = new VKPlatform();